import React, {
  useState,
  useContext,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from 'react';
import { Paper, Box, Typography } from '@mui/material';
import AuthContext from '../../../../contexts/AuthContext';
import { useClinicalSession } from '../../../../contexts/ClinicalSessionContext';
import useAllStudentBehaviorData from '../../../../hooks/useAllStudentsBehaviorData';
import QuickTrackSearchSection from './components/QuickTrackSearchSection';
import QuickTrackTabsSection from './components/QuickTrackTabsSection';
import useQuickTracksData from './hooks/useQuickTracksData';

const QuickTracksDataCard = forwardRef(({
  sessionId,
  user,
  userName,
  onFieldChange,
  dataEntryRef,
  draftEntries = {},
  onUpsertDraft,
  onRemoveDraft,
  formState,
  originalData,
}, ref) => {
  const { user: currentUser, typeOfUser } = useContext(AuthContext);
  const { getSession } = useClinicalSession();
  const session = getSession(sessionId);
  const sessionData = session?.sessionData || {};

  const [quickTracksSearchTerm, setQuickTracksSearchTerm] = useState('');
  const [quickTracksExpanded, setQuickTracksExpanded] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const quickTrackTabsSectionRef = useRef(null);
  const modalDataRef = useRef({});
  // Cache the saveAllQuickTracks function to survive re-renders
  const cachedSaveAllQuickTracksRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      saveAll: async () => {
        // Try direct ref first
        if (
          quickTrackTabsSectionRef.current &&
          typeof quickTrackTabsSectionRef.current.saveAllQuickTracks === 'function'
        ) {
          return quickTrackTabsSectionRef.current.saveAllQuickTracks();
        }
        
        // Fallback to cached function if ref was cleared by re-render
        if (cachedSaveAllQuickTracksRef.current) {
          return cachedSaveAllQuickTracksRef.current();
        }

        return [];
      },
    }),
    []
  );

  const {
    data: studentBehaviorData = [],
    isLoading: isLoadingStudentBehaviorData,
  } = useAllStudentBehaviorData(currentUser?.uid, typeOfUser);

  const originalQuickTrackIds = useMemo(
    () => originalData?.QuickTracks?.map((track) => track.quickTrackID),
    [originalData?.QuickTracks]
  );

  const originalQuickTrackData = useMemo(
    () => originalData?.QuickTracks || [],
    [originalData?.QuickTracks]
  );

  const {
    filteredQuickTracks,
    displayedQuickTracks,
    selectedQuickTracks,
  } = useQuickTracksData(
    studentBehaviorData,
    user,
    currentUser,
    formState,
    quickTracksSearchTerm,
    quickTracksExpanded,
    sessionData
  );

  // Cache the saveAllQuickTracks function whenever the ref becomes available
  useEffect(() => {
    if (
      quickTrackTabsSectionRef.current &&
      typeof quickTrackTabsSectionRef.current.saveAllQuickTracks === 'function'
    ) {
      cachedSaveAllQuickTracksRef.current = quickTrackTabsSectionRef.current.saveAllQuickTracks.bind(quickTrackTabsSectionRef.current);
    }
  }, [selectedQuickTracks]);

  const handleQuickTrackToggle = (track) => {
    const isAlreadySelected = selectedQuickTracks.some(
      (selectedTrack) => selectedTrack.id === track.id
    );

    const newSelected = isAlreadySelected
      ? selectedQuickTracks.filter((selectedTrack) => selectedTrack.id !== track.id)
      : [...selectedQuickTracks, track];

    if (onFieldChange) {
      onFieldChange(
        'selectedQuickTracks',
        newSelected.map((selectedTrack) => selectedTrack.id)
      );
    }

    if (isAlreadySelected && typeof onRemoveDraft === 'function') {
      onRemoveDraft(track.id);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTabIndex(newValue);
  };

  const registerModalData = (entryKey, getData) => {
    modalDataRef.current[entryKey] = getData;
  };

  useEffect(() => {
    if (selectedQuickTracks.length === 0) {
      setSelectedTabIndex(0);
      return;
    }

    if (selectedTabIndex >= selectedQuickTracks.length) {
      setSelectedTabIndex(selectedQuickTracks.length - 1);
    }
  }, [selectedQuickTracks, selectedTabIndex]);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#f8f9ff',
          borderBottom: '1px solid #e8e9ff',
          px: 3,
          py: 2,
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontWeight: 600,
            color: '#9291ef',
          }}
        >
          QuickTracks ({selectedQuickTracks.length})
        </Typography>
      </Box>

      <QuickTrackSearchSection
        quickTracksSearchTerm={quickTracksSearchTerm}
        onSearchChange={setQuickTracksSearchTerm}
        displayedQuickTracks={displayedQuickTracks}
        filteredQuickTracks={filteredQuickTracks}
        quickTracksExpanded={quickTracksExpanded}
        onToggleExpanded={() => setQuickTracksExpanded(!quickTracksExpanded)}
        selectedQuickTracks={selectedQuickTracks}
        onQuickTrackToggle={handleQuickTrackToggle}
        originalQuickTrackIds={originalQuickTrackIds}
        isLoadingStudentBehaviorData={isLoadingStudentBehaviorData}
      />

      <QuickTrackTabsSection
        ref={quickTrackTabsSectionRef}
        selectedQuickTracks={selectedQuickTracks}
        selectedTabIndex={selectedTabIndex}
        onTabChange={handleTabChange}
        user={user}
        userName={userName}
        originalQuickTrackData={originalQuickTrackData}
        dataEntryRef={dataEntryRef}
        studentBehaviorData={studentBehaviorData}
        formState={formState}
        registerModalData={registerModalData}
        draftEntries={draftEntries}
        onUpsertDraft={onUpsertDraft}
        onRemoveDraft={onRemoveDraft}
      />
    </Paper>
  );
});

export default QuickTracksDataCard;
